import { useCallback } from 'react';
import { TStatementBalance } from '@/services/types/WalletData';
import {
  useStatementCacheContext,
  TStatementCacheEntry
} from '@/features/selectedAccount/context/StatementCacheContext';

export type { TStatementCacheEntry };

const buildKey = (accountId: string, walletId: string): string => `${accountId}_${walletId}`;

export const useStatementCache = (accountId: string | undefined, walletId: string) => {
  const { getEntry, setEntry } = useStatementCacheContext();
  const key = accountId ? buildKey(accountId, walletId) : null;

  const getCache = useCallback((): TStatementCacheEntry | null => {
    if (!key) return null;
    return getEntry(key) ?? null;
  }, [key, getEntry]);

  const setBalances = useCallback(
    (balances: TStatementBalance[], firstDate?: string, lastDate?: string): void => {
      if (!key) return;
      const existing = getEntry(key);
      setEntry(key, {
        balances,
        pdfUrls: existing?.pdfUrls ?? {},
        firstDate: firstDate ?? existing?.firstDate,
        lastDate: lastDate ?? existing?.lastDate
      });
    },
    [key, getEntry, setEntry]
  );

  const setPdfUrl = useCallback(
    (year: number, month: number, url: string): void => {
      if (!key) return;
      const existing = getEntry(key);
      if (!existing) return;
      setEntry(key, { ...existing, pdfUrls: { ...existing.pdfUrls, [`${year}-${month}`]: url } });
    },
    [key, getEntry, setEntry]
  );

  return { getCache, setBalances, setPdfUrl };
};
